import { PageLayout } from '@/layouts/page-layout'
import { useTranslation } from 'react-i18next'
import { useState, useRef, useEffect } from 'react'
import {
  Table,
  Header,
  HeaderRow,
  HeaderCell,
  Body,
  Row,
  Cell
} from '@table-library/react-table-library/table'
import { useTheme } from '@table-library/react-table-library/theme'
import { Button } from '@material-tailwind/react'
import {
  monthOptions,
  yearOptions
} from '@/components/attachment-uploader/utils'
import { FiTrash2, FiUpload, FiX } from 'react-icons/fi'
import CustomSelect from '@/shared/components/custom-select'
import { fileToAttachment } from '@/utils/file-to-attachment'
import { DOCUMENT_UPLOAD_ACCEPT, filterValidDocumentFiles } from '@/utils/document-upload'
import { importApi, attachmentTypeApi } from '@/store/services'
import { store } from '@/store'
import { MassiveImportFilesList } from '@/shared/components/massive-import-files-list'
import { MassiveImportNativeSelect } from '@/shared/components/massive-import-native-select'
import { MassiveImportResultModal } from '@/shared/components/massive-import-result-modal'
import { massiveImportSelectStyles } from '@/shared/components/massive-import-select-styles'
import { normalizeMassiveImportErrors } from '@/utils/massive-import-report'
import { getValidMassiveImportFiles } from '@/utils/massive-import-file-validation'

export const AssetMassiveImport = () => {
  const { t } = useTranslation()
  const theme = useTheme({
    Table: `
      --data-table-library_grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
      table-layout: fixed;
    `,
    Cell: `
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 72px !important;
      padding-top: 8px;
      padding-bottom: 8px;
    `
  })

  const initialValue = {
    id: Date.now(),
    attachment_type: {},
    period_month: '',
    period_year: '',
    files: []
  }

  const [rows, setRows] = useState([initialValue])
  const [attachmentTypes, setAttachmentTypes] = useState([])
  const inputRefs = useRef([])
  const [showNotificationModal, setShowNotificationModal] = useState(false)
  const [iconTypeNotificacionModal, setIconTypeNotificacionModal] = useState('')
  const [titleNotificacionModal, setTitleNotificationModal] = useState('')
  const [messageNotificacionModal, setMessageNotificationModal] = useState('')
  const [notificationErrors, setNotificationErrors] = useState([])

  const addRow = () => {
    setRows((prevRows) => [...prevRows, { ...initialValue, id: Date.now() }])
  }

  const removeRow = (id) => {
    setRows((prevRows) => prevRows.filter((row) => row.id !== id))
  }

  useEffect(() => {
    const fetchAttachmentTypes = async () => {
      try {
        const { attachmentTypes } = await store
          .dispatch(
            attachmentTypeApi.endpoints.getAttachmentTypes.initiate(
              '?page=1&per_page=9999&klass=Contractor::Asset'
            )
          )
          .unwrap()

        setAttachmentTypes(
          attachmentTypes.map((option) => ({
            value: option.id,
            label: option.name,
            id: option.id,
            name: option.name,
            recurrence: option.recurrence
          }))
        )
      } catch (error) {
        console.error('Error fetching attachment types:', error)
      }
    }

    fetchAttachmentTypes()
  }, [])

  const handleInputChange = (id, field, value) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id
          ? { ...row, [field]: value }
          : row
      )
    )
  }

  const clearFiles = (id) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id ? { ...row, files: [] } : row
      )
    )
  }

  const removeFile = (id, fileIndex) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id
          ? { ...row, files: row.files.filter((_, index) => index !== fileIndex) }
          : row
      )
    )
  }

  const updateRowFiles = (id, newFiles, append = false) => {
    const { validFiles, errors } = filterValidDocumentFiles(newFiles)

    if (errors.length > 0) {
      alert(errors.join('\n'))
    }

    if (validFiles.length === 0) {
      return
    }

    Promise.all(
      validFiles.map((file) =>
        new Promise((resolve) => {
          fileToAttachment(file, resolve)
        })
      )
    ).then((attachments) => {
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.id === id
            ? { ...row, files: append ? [...row.files, ...attachments] : attachments }
            : row
        )
      )
    })
  }

  const handleFileChange = (event, id) => {
    const files = Array.from(event.target.files || [])

    if (files.length === 0) {
      event.target.value = ''
      return
    }

    updateRowFiles(id, files)
    event.target.value = ''
  }

  const handleClick = (index) => {
    inputRefs.current[index].click()
  }

  const validateRows = () => {
    for (const row of rows) {
      if (!row.attachment_type.value) {
        alert('Por favor selecciona un Tipo de documento en todas las filas.')
        return false
      }

      if (
        ['monthly'].includes(row.attachment_type.recurrence) &&
        !row.period_month
      ) {
        alert('Por favor selecciona un mes en todas las filas requeridas.')
        return false
      }

      if (
        ['monthly', 'yearly'].includes(row.attachment_type.recurrence) &&
        !row.period_year
      ) {
        alert('Por favor selecciona un año en todas las filas requeridas.')
        return false
      }

      if (row.files.length === 0) {
        alert('Por favor sube al menos un archivo en cada fila.')
        return false
      }
    }
    return true
  }

  const uploadRows = async () => {
    if (!validateRows()) {
      return
    }
    try {
      const sanitizedRows = rows.map((row) => ({
        ...row,
        period_month: row.period_month,
        period_year: row.period_year,
        files: getValidMassiveImportFiles(row.files, 'asset')
      }))
      const hasAnyUploadableFiles = sanitizedRows.every((row) => row.files.length > 0)

      if (!hasAnyUploadableFiles) {
        alert('Cada fila debe tener al menos un archivo con un nombre valido antes de cargar.')
        return
      }

      const { type, body } = { type: 'assets', body: { data: sanitizedRows } }
      const response = await store.dispatch(
        importApi.endpoints.sendAttachmentFile.initiate({ type, body })
      ).unwrap()

      if (response.status_upload === 200) {
        setRows([initialValue])
        setIconTypeNotificacionModal('check')
        setTitleNotificationModal('Importación realizada exitosamente.')
        setMessageNotificationModal('Se importaron correctamente todos los archivos')
        setNotificationErrors([])
      } else {
        setIconTypeNotificacionModal('error')
        setTitleNotificationModal('Importación realizada con errores')
        setMessageNotificationModal(
          'Revisa el detalle de errores de la importación en este listado.'
        )
        setNotificationErrors(normalizeMassiveImportErrors(response.errors))
      }
      setShowNotificationModal(true)
    } catch (error) {
      setNotificationErrors([])
      if (error && error.originalStatus === 413) {
        setIconTypeNotificacionModal('error')
        setTitleNotificationModal('No se pudo realizar la importación')
        setMessageNotificationModal(
          'Se superó el tamaño máximo de carga.'
        )
      } else {
        setIconTypeNotificacionModal('error')
        setTitleNotificationModal('No se pudo realizar la importación')
        setMessageNotificationModal(
          'Ha ocurrido un error con el servidor. Vuelve a intentarlo.'
        )
      }
      setShowNotificationModal(true)
    }
  }

  return (
    <PageLayout title={t('asset.attachmentImport')}>
      {showNotificationModal && (
        <MassiveImportResultModal
          title={titleNotificacionModal}
          message={messageNotificacionModal}
          iconType={iconTypeNotificacionModal}
          errors={notificationErrors}
          onClose={setShowNotificationModal}
        />
      )}
      <Table
        data={{ nodes: rows }}
        layout={{ custom: true, fixedHeader: true }}
        theme={theme}
        className='h-max'
      >
        {(tableList) => (
          <>
            <Header>
              <HeaderRow>
                <HeaderCell>Tipo</HeaderCell>
                <HeaderCell>Mes</HeaderCell>
                <HeaderCell>Año</HeaderCell>
                <HeaderCell>Archivos</HeaderCell>
                <HeaderCell>Acciones</HeaderCell>
              </HeaderRow>
            </Header>

            <Body>
              {tableList.map((column, index) => (
                <Row key={column.id} item={column}>
                  <div>
                    <Cell>
                      <CustomSelect
                        options={attachmentTypes}
                        value={attachmentTypes.find(
                          (option) =>
                            option.value === column.attachment_type.value
                        )}
                        onChange={(selectedOption) =>
                          handleInputChange(
                            column.id,
                            'attachment_type',
                            selectedOption || ''
                          )
                        }
                        menuPortalTarget={document.body}
                        styles={massiveImportSelectStyles}
                      />
                    </Cell>
                  </div>
                  <div>
                    <Cell>
                      {['monthly'].includes(
                        attachmentTypes.find(
                          (option) =>
                            option.value === column.attachment_type.value
                        )?.recurrence
                      )
                        ? (
                        <MassiveImportNativeSelect
                          options={monthOptions}
                          value={column.period_month}
                          onChange={(selectedValue) =>
                            handleInputChange(column.id, 'period_month', selectedValue ? Number(selectedValue) : '')
                          }
                        />
                          )
                        : (
                        <>-</>
                          )}
                    </Cell>
                  </div>
                  <div>
                    <Cell>
                      {['monthly', 'yearly'].includes(
                        attachmentTypes.find(
                          (option) =>
                            option.value === column.attachment_type.value
                        )?.recurrence
                      )
                        ? (
                        <MassiveImportNativeSelect
                          options={yearOptions()}
                          value={column.period_year}
                          onChange={(selectedValue) =>
                            handleInputChange(column.id, 'period_year', selectedValue ? Number(selectedValue) : '')
                          }
                        />
                          )
                        : (
                        <>-</>
                          )}
                    </Cell>
                  </div>
                  <Cell>
                    <div className='flex items-center justify-center gap-2'>
                      <input
                        type='file'
                        ref={(el) => (inputRefs.current[index] = el)}
                        multiple
                        accept={DOCUMENT_UPLOAD_ACCEPT}
                        style={{ display: 'none' }}
                        onChange={(event) => handleFileChange(event, column.id)}
                      />
                      <Button
                        variant='text'
                        className='p-2 rounded-full bg-blue-50'
                        onClick={() => handleClick(index)}
                      >
                        <FiUpload size={20} color='#1976d2' />
                      </Button>
                      {column.files.length > 0 && (
                        <Button
                          variant='text'
                          className='ml-2 p-2 rounded-full bg-red-50'
                          onClick={() => clearFiles(column.id)}
                        >
                          <FiX size={20} color='#ef4444' />
                        </Button>
                      )}
                      <MassiveImportFilesList
                        files={column.files}
                        onClear={() => clearFiles(column.id)}
                        onRemoveFile={(fileIndex) => removeFile(column.id, fileIndex)}
                        onAddFiles={(files) => updateRowFiles(column.id, files, true)}
                        accept={DOCUMENT_UPLOAD_ACCEPT}
                        validationType='asset'
                      />
                    </div>
                  </Cell>
                  <Cell>
                    <div className='flex items-center space'>
                      <Button onClick={() => removeRow(column.id)} color='red'>
                        <FiTrash2 />
                      </Button>
                    </div>
                  </Cell>
                </Row>
              ))}
            </Body>
          </>
        )}
      </Table>
      <div className='flex justify-between items-center'>
        <Button variant='outlined' color='white' onClick={addRow}>Agregar Fila</Button>
        <Button onClick={() => uploadRows()}>Cargar</Button>
      </div>
    </PageLayout>
  )
}
